/**
 * AveragesPanel — displays computed averages from the bulletin read-model.
 *
 * The bulletin resource returns whatever BulletinService::compute() builds —
 * a plain object. We render known keys and fall back gracefully for others.
 *
 * Expected bulletin shape (from BulletinService):
 *   {
 *     student_id, period_id,
 *     subjects: [
 *       {
 *         subject_id, subject_name,
 *         average,          // weighted average for this subject
 *         ie_average,       // average of IE scores
 *         ds_average,       // average of DS scores
 *         ie_count, ds_count,
 *         config: { n_ie, n_ds, ie_weight, ds_weight }
 *       }
 *     ],
 *     general_average    // cross-subject average
 *   }
 *
 * Props:
 *   bulletin      {Object|null}
 *   isLoading     {boolean}
 *   subjectId     {number|null}  — highlight the active subject row
 */

const AveragesPanel = ({ bulletin, isLoading, subjectId }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <span className="text-sm font-montserrat text-gray-400 animate-pulse">
          Calcul des moyennes…
        </span>
      </div>
    );
  }

  if (!bulletin) {
    return (
      <p className="text-sm font-montserrat text-gray-400 py-4">
        Sélectionnez un contrat et une période pour voir les moyennes.
      </p>
    );
  }

  const subjects = bulletin.subjects ?? [];
  const generalAverage = bulletin.general_average;

  const fmt = (val) =>
    val !== null && val !== undefined ? Number(val).toFixed(2) : '—';

  return (
    <div className="space-y-3" aria-label="Moyennes">
      <h3 className="font-montserrat font-semibold text-[#17386E] text-sm">
        Moyennes
      </h3>

      {subjects.length === 0 && (
        <p className="text-sm font-montserrat text-gray-400">
          Aucune note saisie pour cette période.
        </p>
      )}

      {subjects.length > 0 && (
        <table className="w-full text-sm font-montserrat border-collapse">
          <thead>
            <tr className="border-b border-gray-100">
              <th className="text-left py-1 pr-3 text-[#4A4A4A] font-semibold">Matière</th>
              <th className="text-center py-1 px-2 text-[#4A4A4A] font-semibold">Moy. IE</th>
              <th className="text-center py-1 px-2 text-[#4A4A4A] font-semibold">Moy. DS</th>
              <th className="text-center py-1 px-2 text-[#17386E] font-semibold">Moyenne</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((s) => {
              const active = subjectId && String(s.subject_id) === String(subjectId);
              return (
                <tr
                  key={s.subject_id}
                  className={active ? 'bg-[#EAF4FB] rounded' : ''}
                >
                  <td className="py-1.5 pr-3 text-[#4A4A4A]">{s.subject_name ?? '—'}</td>
                  <td className="py-1.5 px-2 text-center text-[#4A4A4A]">
                    {fmt(s.ie_average)}
                  </td>
                  <td className="py-1.5 px-2 text-center text-[#4A4A4A]">
                    {fmt(s.ds_average)}
                  </td>
                  <td className="py-1.5 px-2 text-center font-semibold text-[#17386E]">
                    {fmt(s.average)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {generalAverage !== undefined && generalAverage !== null && (
            <tfoot>
              <tr className="border-t border-gray-200">
                <td className="pt-2 pr-3 font-semibold text-[#4A4A4A]" colSpan={3}>
                  Moyenne générale
                </td>
                <td className="pt-2 px-2 text-center font-bold text-[#FFA726]">
                  {fmt(generalAverage)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
};

export default AveragesPanel;
